import { Button } from "@/components/ui/button";
import {
    ArForm,
    ArNumberFormField,
    ArTextAreaFormField,
} from "@/v2/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import FindOnePayslip from "@/v2/api/payslip/FindOne";
import { useNavigate } from "react-router-dom";
import { ArOfficeStaffPayslipFormSchema } from "@/v2/schema/payslip";
import { Employee } from "@/v2/types";
import LoadingAnimation from "@/v2/components/ui/LoadingAnimation";
import { ArEmployeeComboBox } from "@/v2/components/ui/combo-box";
import CreatePayslip from "@/v2/api/payslip/Create";
import UpdatePayslip from "@/v2/api/payslip/Update";
import { MonthSelectField } from "../MonthSelectField";
import { ArSwitchFormField } from "@/v2/components/ui/form/SwitchFormField";

export default function ArOfficeStaffPayslipForm({ id }: { id?: string }) {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const [_emp, setEmp] = useState<Employee | null>(null);

    const query = useQuery({
        queryKey: ["payslips", id],
        queryFn: () => FindOnePayslip(id!),
        enabled: !!id,
    });

    const form = useForm<ArOfficeStaffPayslipFormSchema>({
        resolver: zodResolver(ArOfficeStaffPayslipFormSchema),
        defaultValues: {
            employee_id: "",
            month: new Date().getMonth() + 1,
            year: new Date().getFullYear(),
            basic_salary: 0,
            commission: 0,
            absent_deduction: 0,
            advance_or_other_deduction: 0,
            balance_to_deduct: 0,
            food_allowance: 0,
            food_allowance_by_ar: false,
            net_amount: 0,
            food_allowance_note: "",
            note: "",
            status: "DRAFT",
            type: "MONTHLY_STAFF",
            eid_bonus: 0,
        },
    });

    const { reset } = form;

    // Watch every field that feeds into the net amount calculation
    const [
        basic_salary,
        commission,
        food_allowance,
        eid_bonus,
        absent_deduction,
        advance_or_other_deduction,
        food_allowance_by_ar,
    ] = useWatch({
        control: form.control,
        name: [
            "basic_salary",
            "commission",
            "food_allowance",
            "eid_bonus",
            "absent_deduction",
            "advance_or_other_deduction",
            "food_allowance_by_ar",
        ],
    });

    // Auto-calculate net amount whenever an earning or deduction changes
    useEffect(() => {
        const total =
            (Number(basic_salary) || 0) +
            (Number(commission) || 0) +
            (Number(food_allowance) || 0) +
            (Number(eid_bonus) || 0) -
            (Number(absent_deduction) || 0) -
            (Number(advance_or_other_deduction) || 0);

        form.setValue("net_amount", total, {
            shouldValidate: true,
            shouldDirty: true,
        });
    }, [
        basic_salary,
        commission,
        food_allowance,
        eid_bonus,
        absent_deduction,
        advance_or_other_deduction,
        form,
    ]);

    useEffect(() => {
        if (query.data && !form.formState.isDirty) {
            reset({
                employee_id: query.data.employee_id ?? "",
                month: query.data.month ?? new Date().getMonth() + 1,
                year: query.data.year ?? new Date().getFullYear(),
                basic_salary: query.data.basic_salary ?? 0,
                commission: query.data.commission ?? 0,
                absent_deduction: query.data.absent_deduction ?? 0,
                advance_or_other_deduction:
                    query.data.advance_or_other_deduction ?? 0,
                balance_to_deduct: query.data.balance_to_deduct ?? 0,
                food_allowance: query.data.food_allowance ?? 0,
                net_amount: query.data.net_amount ?? 0,
                food_allowance_note: query.data.food_allowance_note ?? "",
                note: query.data.note ?? "",
                eid_bonus: query.data?.eid_bonus || 0,
                status: "DRAFT",
                type: "MONTHLY_STAFF",
                food_allowance_by_ar:
                    query?.data?.food_allowance_by_ar || false,
            });
        }
    }, [query.data, reset, form.formState.isDirty]);

    useEffect(() => {
        if (food_allowance_by_ar) {
            form.setValue("food_allowance", 0);
        }
    }, [food_allowance_by_ar]);

    const CreateMutation = useMutation({
        mutationFn: (data: ArOfficeStaffPayslipFormSchema) =>
            CreatePayslip(data),
    });

    const EditMutation = useMutation({
        mutationFn: (data: ArOfficeStaffPayslipFormSchema) =>
            UpdatePayslip({ id: id!, data }),
    });

    const handleFormSubmit = (data: ArOfficeStaffPayslipFormSchema) => {
        const mutation = id ? EditMutation : CreateMutation;

        const action = id ? "updated" : "created";

        const transformedData: ArOfficeStaffPayslipFormSchema = {
            ...data,
            status: "DRAFT",
            type: "MONTHLY_STAFF",
        };

        mutation.mutate(transformedData, {
            onError: (err) => {
                console.error({ err });
                toast.error("Oops! Something went wrong", {
                    description: `While ${id ? "updating" : "creating"} the payslip, we encountered an issue.`,
                });
            },
            onSuccess: (res) => {
                toast.success(`Payslip ${action} successfully`);
                navigate(`/payslips/ar-office-staff/${res.id}`);
                queryClient.invalidateQueries({
                    queryKey: ["payslips"],
                });
            },
        });
    };

    if (id && query.isLoading) {
        return <LoadingAnimation />;
    }

    if (id && query.isError) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="text-center">
                    <p className="mb-4 text-red-600">Failed to load payslip</p>
                </div>
            </div>
        );
    }

    return (
        <ArForm
            form={form}
            formSchema={ArOfficeStaffPayslipFormSchema}
            onSubmit={handleFormSubmit}
        >
            <div className="space-y-6">
                {/* Basic Information */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Basic Information</h3>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <ArEmployeeComboBox<ArOfficeStaffPayslipFormSchema>
                            name="employee_id"
                            defaultValue={query?.data?.employee ?? null}
                            setSelectedEmployee={setEmp}
                        />
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <MonthSelectField
                                control={form.control}
                                name="month"
                                label="Month"
                            />
                            <ArNumberFormField<ArOfficeStaffPayslipFormSchema>
                                label="Year"
                                name="year"
                                placeholder="Enter year"
                            />
                        </div>
                    </div>
                </div>

                {/* Earnings */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Earnings</h3>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <ArNumberFormField<ArOfficeStaffPayslipFormSchema>
                            label="Basic Salary"
                            name="basic_salary"
                            placeholder="Enter basic salary"
                        />
                        <ArNumberFormField<ArOfficeStaffPayslipFormSchema>
                            label="Commission"
                            name="commission"
                            placeholder="Enter commission"
                        />
                        <ArNumberFormField<ArOfficeStaffPayslipFormSchema>
                            label="Bonus"
                            name="eid_bonus"
                            placeholder="Enter bonus amount"
                        />
                        <ArNumberFormField<ArOfficeStaffPayslipFormSchema>
                            label="Food Allowance"
                            name="food_allowance"
                            placeholder="Enter food allowance"
                            disabled={food_allowance_by_ar}
                        />
                        <ArSwitchFormField<ArOfficeStaffPayslipFormSchema>
                            name="food_allowance_by_ar"
                            label="Food Allowance By AR"
                        />
                    </div>
                </div>

                {/* Deductions */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Deductions</h3>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <ArNumberFormField<ArOfficeStaffPayslipFormSchema>
                            label="Absent Deduction"
                            name="absent_deduction"
                            placeholder="Enter absent deduction"
                        />
                        <ArNumberFormField<ArOfficeStaffPayslipFormSchema>
                            label="Advance / Other Deduction"
                            name="advance_or_other_deduction"
                            placeholder="Enter advance or other deduction"
                        />
                        <ArNumberFormField<ArOfficeStaffPayslipFormSchema>
                            label="Balance to Deduct"
                            name="balance_to_deduct"
                            placeholder="Enter balance to deduct"
                        />
                    </div>
                </div>

                {/* Totals */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Totals</h3>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <ArNumberFormField<ArOfficeStaffPayslipFormSchema>
                            label="Net Amount"
                            name="net_amount"
                            placeholder="Enter net amount"
                            disabled
                        />
                    </div>
                </div>

                {/* Notes */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Notes</h3>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <ArTextAreaFormField<ArOfficeStaffPayslipFormSchema>
                            label="Food Allowance Note"
                            name="food_allowance_note"
                            placeholder="Enter food allowance note"
                        />
                        <ArTextAreaFormField<ArOfficeStaffPayslipFormSchema>
                            label="Note"
                            name="note"
                            placeholder="Enter note"
                        />
                    </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end gap-2 border-t pt-4">
                    <Button
                        type="submit"
                        className="min-w-[160px]"
                        disabled={
                            CreateMutation.isPending || EditMutation.isPending
                        }
                    >
                        {CreateMutation.isPending || EditMutation.isPending
                            ? "Saving..."
                            : id
                                ? "Update Payslip"
                                : "Create Payslip"}
                    </Button>
                </div>
            </div>
        </ArForm>
    );
}
